import type { HexLibraryBook } from '@/components/HexLibrary';

interface BookModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (e: React.FormEvent) => void;
    editingBook: HexLibraryBook | null;
    bookTitle: string;
    setBookTitle: (title: string) => void;
    bookDescription: string;
    setBookDescription: (description: string) => void;
    bookHue: number;
    setBookHue: (hue: number) => void;
    isSaving: boolean;
}

export function BookModal({
    isOpen,
    onClose,
    onSubmit,
    editingBook,
    bookTitle,
    setBookTitle,
    bookDescription,
    setBookDescription,
    bookHue,
    setBookHue,
    isSaving,
}: BookModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">
                    {editingBook ? 'Edit Book' : 'Create New Book'}
                </h3>
                <form onSubmit={onSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Book Title
                        </label>
                        <input
                            type="text"
                            value={bookTitle}
                            onChange={(e) => setBookTitle(e.target.value)}
                            placeholder="e.g., History of the Land..."
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description (optional)
                        </label>
                        <textarea
                            value={bookDescription}
                            onChange={(e) => setBookDescription(e.target.value)}
                            placeholder="A brief description of this book..."
                            rows={2}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Accent Color
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="range"
                                min={0}
                                max={360}
                                value={bookHue}
                                onChange={(e) => setBookHue(parseInt(e.target.value))}
                                className="flex-1"
                            />
                            <div
                                className="w-10 h-10 rounded-lg border border-slate-600"
                                style={{ backgroundColor: `hsl(${bookHue}, 50%, 35%)` }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!bookTitle.trim() || isSaving}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                        >
                            {isSaving ? 'Saving...' : editingBook ? 'Save Changes' : 'Create Book'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
